import { useState, useEffect } from 'react'
import { PortalLayout } from '../components/PortalLayout'
import { api } from '../api'
import { useAuth } from '../contexts/AuthContext'

interface Driver {
  id: number
  first_names: string
  surname: string
  card_number: string | null
  date_of_birth: string | null
  licence_number: string | null
  licence_authority: string | null
  card_issuing_authority: string | null
  card_valid_from: string | null
  card_expiry: string | null
  depot_id: number | null
  depot_name: string | null
  depot_code: string | null
  assigned_manager_id: number | null
  assigned_manager_name: string | null
  user_id: number | null
  linked_user_name: string | null
  last_download: string | null
  phone?: string | null
  email?: string | null
}

interface PortalUser {
  id: number
  first_name: string
  last_name: string
  email: string
  role: string
}

interface Depot {
  id: number
  name: string
  code: string | null
}

const EMPTY_FORM = {
  first_names: '',
  surname: '',
  card_number: '',
  date_of_birth: '',
  licence_number: '',
  licence_authority: '',
  card_issuing_authority: '',
  card_valid_from: '',
  card_expiry: '',
  depot_id: '',
  phone: '',
  email: '',
  user_id: '',
}

export function DriversPage() {
  const { user } = useAuth()
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [depots, setDepots] = useState<Depot[]>([])
  const [portalUsers, setPortalUsers] = useState<PortalUser[]>([])
  const [pagination, setPagination] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [depotFilter, setDepotFilter] = useState<number | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Form state
  const [showForm, setShowForm] = useState(false)
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)

  // Detail view
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)
  const [downloads, setDownloads] = useState<any[]>([])
  const [loadingDownloads, setLoadingDownloads] = useState(false)

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearchDebounced(search); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [search])

  // Load data
  useEffect(() => { loadDrivers() }, [searchDebounced, depotFilter, page])
  useEffect(() => { api.getDepots().then(setDepots).catch(() => {}) }, [])
  useEffect(() => { api.listUsers().then(setPortalUsers).catch(() => {}) }, [])

  async function loadDrivers() {
    setLoading(true)
    try {
      const data = await api.getDrivers({
        search: searchDebounced || undefined,
        depot_id: depotFilter,
        page,
        page_size: 25,
      })
      if (Array.isArray(data)) {
        setDrivers(data)
        setPagination(null)
      } else {
        setDrivers(data.drivers || [])
        setPagination(data.pagination || null)
      }
    } catch (e: any) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to load drivers')
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setEditingDriver(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
  }

  function openEdit(driver: Driver) {
    setEditingDriver(driver)
    setForm({
      first_names: driver.first_names || '',
      surname: driver.surname || '',
      card_number: driver.card_number || '',
      date_of_birth: driver.date_of_birth || '',
      licence_number: driver.licence_number || '',
      licence_authority: driver.licence_authority || '',
      card_issuing_authority: driver.card_issuing_authority || '',
      card_valid_from: driver.card_valid_from || '',
      card_expiry: driver.card_expiry || '',
      depot_id: driver.depot_id ? String(driver.depot_id) : '',
      phone: (driver as any).phone || '',
      email: (driver as any).email || '',
      user_id: driver.user_id ? String(driver.user_id) : '',
    })
    setShowForm(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSuccess('')
    const payload = {
      ...form,
      depot_id: form.depot_id ? Number(form.depot_id) : null,
      card_number: form.card_number || null,
      user_id: form.user_id ? Number(form.user_id) : null,
    }
    try {
      if (editingDriver) {
        await api.updateDriver(editingDriver.id, payload)
        setSuccess(`${form.first_names} ${form.surname} updated`)
      } else {
        await api.createDriver(payload)
        setSuccess(`${form.first_names} ${form.surname} added`)
      }
      setShowForm(false)
      loadDrivers()
    } catch (e: any) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to save driver')
    }
  }

  async function handleDelete(driver: Driver) {
    const name = `${driver.first_names} ${driver.surname}`
    if (!confirm(`Permanently delete ${name} and ALL their data (card downloads, infringements, DCRS records, letters)? This cannot be undone.`)) return
    setError('')
    setSuccess('')
    try {
      await api.deleteDriver(driver.id)
      setSuccess(`${name} deleted`)
      if (selectedDriver?.id === driver.id) setSelectedDriver(null)
      loadDrivers()
    } catch (e: any) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to delete driver')
    }
  }

  async function selectDriver(driver: Driver) {
    setSelectedDriver(driver)
    setLoadingDownloads(true)
    try {
      const dl = await api.getDriverDownloads(driver.id)
      setDownloads(dl || [])
    } catch { setDownloads([]) }
    finally { setLoadingDownloads(false) }
  }

  return (
    <PortalLayout currentPage="drivers">
      <div className="p-5 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Drivers</h1>
          <button
            onClick={openCreate}
            className="px-3 py-1.5 bg-slate-700 text-white rounded text-xs font-medium hover:bg-slate-800"
          >
            + Add Driver
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
            {error}
            <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 text-sm rounded p-3">
            {success}
            <button onClick={() => setSuccess('')} className="ml-2 font-medium underline">Dismiss</button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by name, card number, licence..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 max-w-sm px-4 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          />
          {depots.length > 0 && (
            <select
              value={depotFilter ?? ''}
              onChange={(e) => { setDepotFilter(e.target.value ? Number(e.target.value) : undefined); setPage(1) }}
              className="px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
            >
              <option value="">All Depots</option>
              {depots.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <span className="text-sm text-gray-400">
            {pagination ? `${pagination.total} driver${pagination.total !== 1 ? 's' : ''}` : `${drivers.length} drivers`}
          </span>
        </div>

        {/* Driver table */}
        <div className="bg-white rounded border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <div className="animate-spin w-8 h-8 border-4 border-slate-500 border-t-transparent rounded mx-auto mb-3" />
              Loading drivers...
            </div>
          ) : drivers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="mb-2">{searchDebounced ? 'No matching drivers' : 'No drivers yet'}</p>
              {!searchDebounced && (
                <button onClick={openCreate} className="text-blue-600 text-sm font-medium hover:underline">
                  Add your first driver
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Card Number</th>
                  <th className="px-4 py-3 font-medium">Licence</th>
                  <th className="px-4 py-3 font-medium">Depot</th>
                  <th className="px-4 py-3 font-medium">Last Download</th>
                  <th className="px-4 py-3 font-medium">Manager</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drivers.map((d) => (
                  <tr
                    key={d.id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedDriver?.id === d.id ? 'bg-blue-50' : ''}`}
                    onClick={() => selectDriver(d)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-200 rounded flex items-center justify-center text-xs font-bold text-blue-700 flex-shrink-0">
                          {d.first_names?.[0]}{d.surname?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{d.first_names} {d.surname}</p>
                          {d.date_of_birth && <p className="text-xs text-gray-400">DOB: {d.date_of_birth}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {d.card_number ? (
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{d.card_number}</span>
                      ) : (
                        <span className="text-xs text-amber-500 italic">No card linked</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{d.licence_number || '—'}</td>
                    <td className="px-4 py-3">
                      {d.depot_name ? (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">{d.depot_name}</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {d.last_download ? (
                        new Date(d.last_download).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                      ) : (
                        <span className="text-xs text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{d.assigned_manager_name || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openEdit(d)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => window.location.href = `/tacho?driver=${d.id}`}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Tacho
                        </button>
                        <button
                          onClick={() => handleDelete(d)}
                          className="text-xs text-red-500 hover:text-red-700 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-400">
              Page {pagination.page} of {pagination.total_pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(pagination.total_pages, page + 1))}
                disabled={page >= pagination.total_pages}
                className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Driver detail panel */}
        {selectedDriver && (
          <div className="mt-6 bg-white rounded border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {selectedDriver.first_names} {selectedDriver.surname}
                </h2>
                <p className="text-sm text-gray-500">
                  {selectedDriver.card_number ? `Card: ${selectedDriver.card_number}` : 'No card linked'}
                  {selectedDriver.licence_number ? ` · Licence: ${selectedDriver.licence_number}` : ''}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => openEdit(selectedDriver)}
                  className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-800"
                >
                  Edit
                </button>
                <button
                  onClick={() => window.location.href = `/tacho?driver=${selectedDriver.id}`}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  View Tacho Analysis
                </button>
                <button onClick={() => setSelectedDriver(null)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">
                  Close
                </button>
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
              <InfoItem label="Card Expiry" value={selectedDriver.card_expiry || '—'} />
              <InfoItem label="Depot" value={selectedDriver.depot_name || 'Unassigned'} />
              <InfoItem label="Transport Manager" value={selectedDriver.assigned_manager_name || 'Unassigned'} />
              <InfoItem label="Linked Portal User" value={selectedDriver.linked_user_name || 'Not linked'} />
              <InfoItem label="Issuing Authority" value={selectedDriver.card_issuing_authority || '—'} />
            </div>

            {/* Card downloads */}
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Card Downloads</h3>
            {loadingDownloads ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : downloads.length === 0 ? (
              <p className="text-sm text-gray-400">No card downloads recorded for this driver.</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {downloads.map((dl: any) => (
                  <div key={dl.id} className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm">
                    <div>
                      <p className="font-medium text-gray-900">
                        {dl.download_datetime ? new Date(dl.download_datetime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {dl.period_start && dl.period_end ? `Period: ${dl.period_start} to ${dl.period_end}` : ''}
                        {dl.total_driving_hours ? ` · ${dl.total_driving_hours.toFixed(1)}h driving` : ''}
                        {dl.infringement_count ? ` · ${dl.infringement_count} infringement${dl.infringement_count !== 1 ? 's' : ''}` : ''}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      dl.source === 'card' ? 'bg-green-100 text-green-700' :
                      dl.source === 'upload' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {dl.source || 'unknown'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add/Edit modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit}>
                <div className="p-6">
                  <h2 className="text-sm font-semibold text-gray-900 mb-4">
                    {editingDriver ? 'Edit Driver' : 'Add New Driver'}
                  </h2>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">First Name(s) *</label>
                      <input
                        type="text" required
                        value={form.first_names}
                        onChange={(e) => setForm({ ...form, first_names: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                        placeholder="e.g. JAMES EDWARD"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Surname *</label>
                      <input
                        type="text" required
                        value={form.surname}
                        onChange={(e) => setForm({ ...form, surname: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                        placeholder="e.g. WILSON"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Tachograph Card Number
                      <span className="text-gray-400 font-normal ml-1">(links to card reader data)</span>
                    </label>
                    <input
                      type="text"
                      value={form.card_number}
                      onChange={(e) => setForm({ ...form, card_number: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400 font-mono"
                      placeholder="e.g. UK00000012345600"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      If provided, card downloads will automatically link to this driver. Leave blank to add later.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date of Birth</label>
                      <input
                        type="date"
                        value={form.date_of_birth}
                        onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Licence Number</label>
                      <input
                        type="text"
                        value={form.licence_number}
                        onChange={(e) => setForm({ ...form, licence_number: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                        placeholder="e.g. SINGL123456AB1CD"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                        placeholder="07700 900000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                        placeholder="driver@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Card Valid From</label>
                      <input
                        type="date"
                        value={form.card_valid_from}
                        onChange={(e) => setForm({ ...form, card_valid_from: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Card Expiry</label>
                      <input
                        type="date"
                        value={form.card_expiry}
                        onChange={(e) => setForm({ ...form, card_expiry: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  {depots.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Depot</label>
                      <select
                        value={form.depot_id}
                        onChange={(e) => setForm({ ...form, depot_id: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="">No depot assigned</option>
                        {depots.map(d => <option key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ''}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Linked Portal User</label>
                    <select
                      value={form.user_id}
                      onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">— No linked user —</option>
                      {portalUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Links this tachograph driver to a portal user account for walkaround checks and app login</p>
                  </div>
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-slate-700 text-white rounded hover:bg-slate-800 font-medium"
                  >
                    {editingDriver ? 'Save Changes' : 'Add Driver'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  )
}


function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-700">{value}</p>
    </div>
  )
}
